/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */

import {useCallback, useRef} from 'react';
import Extent from '@arcgis/core/geometry/Extent';
import {initializeEsriModules} from '../helpers/initializeEsriModules';
import {EXTENT_FACTOR} from '../layers/AssetLayer';

interface ExtentProps {
  xmax: number,
  xmin: number,
  ymax: number,
  ymin: number,
  spatialReference: {
    atestWkid: number,
    wkid: number
  }
};

interface InitialExtentProps {
  map: __esri.Map,
  assetsExtent: __esri.Extent[],
  mapViewExtent?: __esri.Extent[],
  expand?: boolean
};

/**
 * @useViewExtent
 * Hook to return the initial view extent based on given parameters.
 * If any extent is not received then return the default extent from the web map.
 *
 *  ```typescript
 *   import {useViewExtent} from '@/routes/tracking/hooks';
 *
 *   const {initializeViewExtent, getInitialExtent} = useViewExtent();
 *  ```
 */
export const useViewExtent = () => {
  const extentRef = useRef(null);

  // Defines a utility to load the extent object from Esri.
  const initializeViewExtent = useCallback(async function(isFMTracking: boolean = false) {
    if (!isFMTracking) {
      const [Extent]    = await initializeEsriModules('esri/geometry/Extent');
      extentRef.current = Extent;
    } else {
      extentRef.current = Extent;
    }
  }, [extentRef]);

  // Defines a utility to create and return an extent.
  const createExtent = useCallback(({xmax, xmin, ymax, ymin, spatialReference}: ExtentProps) => {
    const Extent = extentRef.current;
    return new Extent(xmin, ymin, xmax, ymax, spatialReference);
  }, [extentRef]);

  // Defines a utility to return the default extent from the webmap.
  const getWebmapExtent = useCallback((map: __esri.Map) => {
    const {viewpoint} = map?.initialViewProperties || {};
    return viewpoint?.targetGeometry?.extent || null;
  }, []);

  // Defines a utility to return combined multiple extents.
  const getExtent = useCallback((extent: __esri.Extent[], expand = true) => {
    const extentLength = extent?.length;
    if (extentRef.current) {
      if (extentLength > 1) {
        let combinedExtent = null;
        extent.forEach(({extent : ex}) => {
          const ext = createExtent(ex as any);
          if (combinedExtent) {
            combinedExtent = combinedExtent.union(ext);
          } else {
            combinedExtent = ext;
          }
        });
        return expand ? combinedExtent.expand(EXTENT_FACTOR) : combinedExtent;
      }
      if (extentLength) {
        const ext = createExtent(extent?.[0]?.extent as any);
        return expand ? ext.expand(EXTENT_FACTOR) : ext;
      }
    }
    return null;
  }, []);

  // Defines a utility to return initial extent based on extent and webmap extent parameters.
  const getInitialExtent = useCallback(({
    map,
    assetsExtent,
    mapViewExtent,
    expand = true
  }: InitialExtentProps) => {
    const extent = mapViewExtent || assetsExtent;
    if ((!extent?.length) && map) {
      return getWebmapExtent(map);
    }
    return extent ? getExtent([...extent], expand ? !mapViewExtent : null) : null;
  }, []);

  return {
    initializeViewExtent,
    getInitialExtent,
    createExtent
  };
};
